import { create } from 'zustand';
import type { Bill } from '@sense/shared';
import { isNativeApp, nativeMessage, SdDevices } from './native';
import { useLang, LANGS } from './i18n';
import { usePaymentAlerts } from './paymentAlerts';

/**
 * Soundbox, like the SME app: the phone says each payment out loud ("Received 500 rupees by UPI"),
 * in English, Tamil or Hindi. The Android app uses the phone's text-to-speech engine (works offline
 * once the voice is installed); browsers use speechSynthesis.
 */
export type VoiceLang = 'en-IN' | 'ta-IN' | 'hi-IN';
export const VOICE_LANGS: Array<{ id: VoiceLang; label: string }> = [
  { id: 'en-IN', label: 'English (India)' },
  { id: 'ta-IN', label: 'தமிழ் (Tamil)' },
  { id: 'hi-IN', label: 'हिन्दी (Hindi)' },
];

interface SoundboxSettings { enabled: boolean; lang: VoiceLang; volume: number; rate: number; cash: boolean; listen: boolean }
const KEY = 'sd.soundbox';
const defaults = (): SoundboxSettings => ({
  enabled: true,
  lang: (LANGS.find((l) => l.id === useLang.getState().lang)?.speech ?? 'en-IN') as VoiceLang,
  volume: 0.8,
  rate: 1,
  cash: false,
  listen: true, // say payments heard from UPI apps (Android, with notification access)
});
const load = (): SoundboxSettings => { try { return { ...defaults(), ...JSON.parse(localStorage.getItem(KEY) ?? '{}') }; } catch { return defaults(); } };

export const useSoundbox = create<SoundboxSettings & { set(p: Partial<SoundboxSettings>): void }>((set, get) => ({
  ...load(),
  set: (p) => {
    set(p);
    const { set: _s, ...rest } = get();
    try { localStorage.setItem(KEY, JSON.stringify(rest)); } catch { /* storage blocked */ }
    pushToNative();
  },
}));

/** The Android payment listener speaks on its own (app closed too), so it keeps a copy of the settings. */
export function pushToNative() {
  if (!isNativeApp()) return;
  const s = useSoundbox.getState();
  void SdDevices.setSoundbox({ enabled: s.enabled, listen: s.listen, lang: s.lang, rate: s.rate, volume: s.volume }).catch(() => {});
}

type Method = 'upi' | 'card' | 'cash' | null;

/** Words for an amount the voice engines read well: "500 rupees 50 paise" (never "₹500.50"). */
export function paymentPhrase(paise: number, method: Method, lang: VoiceLang): string {
  const r = Math.floor(paise / 100);
  const p = paise % 100;
  if (lang === 'ta-IN') {
    const amt = `${r} ரூபாய்${p ? ` ${p} பைசா` : ''}`;
    const how = method === 'upi' ? 'UPI மூலம் ' : method === 'card' ? 'கார்டு மூலம் ' : method === 'cash' ? 'ரொக்கமாக ' : '';
    return `${how}${amt} பெறப்பட்டது`;
  }
  if (lang === 'hi-IN') {
    const amt = `${r} रुपये${p ? ` ${p} पैसे` : ''}`;
    const how = method === 'upi' ? 'UPI से ' : method === 'card' ? 'कार्ड से ' : method === 'cash' ? 'नकद ' : '';
    return `${how}${amt} प्राप्त हुए`;
  }
  const amt = `${r} rupees${p ? ` ${p} paise` : ''}`;
  const how = method === 'upi' ? ' by UPI' : method === 'card' ? ' by card' : method === 'cash' ? ' in cash' : '';
  return `Received ${amt}${how}`;
}

export const canSpeak = () => isNativeApp() || 'speechSynthesis' in window;

/** Say something now with the soundbox settings (or the ones passed). Rejects with a readable message. */
export async function speak(text: string, o: Partial<SoundboxSettings> = {}) {
  const s = { ...useSoundbox.getState(), ...o };
  if (isNativeApp()) {
    try { await SdDevices.speak({ text, lang: s.lang, rate: s.rate, volume: s.volume }); } catch (e) { throw new Error(nativeMessage(e)); }
    return;
  }
  if (!('speechSynthesis' in window)) throw new Error('This browser can’t speak. Use Chrome or the Android app.');
  const u = new SpeechSynthesisUtterance(text);
  u.lang = s.lang;
  u.rate = s.rate;
  u.volume = s.volume;
  const voice = speechSynthesis.getVoices().find((v) => v.lang.replace('_', '-') === s.lang) ?? speechSynthesis.getVoices().find((v) => v.lang.startsWith(s.lang.slice(0, 2)));
  if (voice) u.voice = voice;
  speechSynthesis.speak(u);
}

/** After a sale: announce what came in by UPI and card (and cash, if switched on). Khata is not money in. */
export function announcePayment(bill: Bill) {
  const s = useSoundbox.getState();
  if (!s.enabled || bill.kind !== 0 || !canSpeak()) return;
  const by = (m: string) => bill.payments.filter((p) => p[0] === m).reduce((a, p) => a + p[1], 0);
  // With payment alerts on, the phone already says each UPI payment when the UPI app reports it.
  const heard = usePaymentAlerts.getState().granted === true && s.listen;
  const parts: Array<[Method, number]> = [...(heard ? [] : [['upi', by('upi')] as [Method, number]]), ['card', by('card')], ...(s.cash ? [['cash', by('cash')] as [Method, number]] : [])];
  const paid = parts.filter(([, a]) => a > 0);
  if (!paid.length) return;
  // Change handed back is part of the cash line; announce what the bill took, not what was tendered.
  const total = paid.reduce((a, [, x]) => a + x, 0);
  const amount = Math.min(total, bill.total);
  void speak(paymentPhrase(amount, paid.length === 1 ? paid[0][0] : null, s.lang)).catch(() => {});
}
